// Property case-review API surface.
//
// Property is a separate deployment (ergobalticproperty.dev.api.camcom.ai)
// reached with the same auth token as Motor — picking "Property" on the
// post-login screen just switches localStorage["source_name"] (see Login.js
// and Layout.js) so the shared apiClient's request interceptor routes to this
// host instead.
//
// These are implemented directly against apiClient rather than delegating to
// apiService.js's motor functions: the property backend's /assessment_list/all
// takes a different parameter set (no page_num/claim_state/warning_id, and
// offset-based paging) and returns a different envelope, so sharing motor's
// implementation would send parameters this backend does not accept.
import axios from "axios";
import { apiClient } from "./apiService";
import { getClientEntry } from "../config/clientConfig";

export const PROPERTY_SOURCE_NAME = "ergobalticproperty";

/**
 * Base URL for property inspection photos. The same in every environment.
 *
 * This used to return "/property-media" in development, on the premise that
 * the media host gates images on the portal's session cookie and that
 * localhost would have it withheld as cross-site. Two things were wrong with
 * that. The proxy it points at was never mounted in setupProxy.js, so every
 * property photograph 404'd locally against a path that does not exist. And
 * the premise does not hold — probed with no cookie at all:
 *
 *   GET https://ergobaltic.dev.media.camcom.ai/home/camcom/media_staging/…jpg
 *     200, 220561 bytes, image/jpeg
 *
 * The images are public. Both the single- and double-slash joins return the
 * identical bytes, so no host trickery is needed either. Pointing straight at
 * the client's own media host makes development and production behave the
 * same, which is the only way a local check says anything about a deploy.
 */
export const getPropertyMediaBaseUrl = () =>
  getClientEntry(PROPERTY_SOURCE_NAME)?.mediaBaseUrl || "";

/**
 * POST /assessment_list/all — the property case list.
 *
 * Response envelope: { Status, data: { "0": {...}, ... }, next: {limit,
 * offset} | null, previous, total_countOfClaims }. total_countOfClaims is the
 * figure behind the list page's "Total Inspections" tile — there is no
 * separate /assessment_report call in the property flow.
 */
export const getPropertyAssessmentList = async ({
  start_date,
  stop_date,
  per_page_limit = 15,
  countryname = "",
  offset = 0,
} = {}) => {
  const params = {
    source_name: PROPERTY_SOURCE_NAME,
    per_page_limit: String(per_page_limit),
    countryname,
  };
  if (start_date) params.start_date = start_date;
  if (stop_date) params.stop_date = stop_date;
  if (offset) params.offset = String(offset);

  const { data } = await apiClient.post(
    "/assessment_list/all",
    new URLSearchParams(params),
  );
  return data ?? null;
};

/**
 * GET /get_analytics_data — everything the analytics page shows.
 *
 * Returns { data: [...records], total_count, passed_count, failed_count,
 * failed_reason_breakdown, trinetra_links_summary, page_num, ... }. The
 * per-record aggregates (averages, distributions) are computed client-side
 * from `data` — see adapters/propertyAnalytics.js.
 */
export const getPropertyAnalytics = async ({
  start_date,
  stop_date,
  per_page_limit = 1000,
  page_num = 1,
} = {}) => {
  const params = {
    source_name: PROPERTY_SOURCE_NAME,
    per_page_limit: String(per_page_limit),
    page_num: String(page_num),
  };
  if (start_date) params.start_date = start_date;
  if (stop_date) params.stop_date = stop_date;

  const { data } = await apiClient.get("/get_analytics_data", { params });
  return data ?? null;
};

// POST /client_demo — per-client demo/limits record the portal's list page
// loads alongside the case list.
export const getPropertyClientDemo = async () => {
  const { data } = await apiClient.post(
    "/client_demo",
    new URLSearchParams({ client_source_name: PROPERTY_SOURCE_NAME }),
  );
  return data ?? null;
};

// GET /client_config?source_name= — per-client capture/config flags.
export const getPropertyClientConfig = async () => {
  const { data } = await apiClient.get("/client_config", {
    params: { source_name: PROPERTY_SOURCE_NAME },
  });
  return data ?? null;
};

// POST /useridList — the client's user accounts.
export const getPropertyUseridList = async () => {
  const { data } = await apiClient.post(
    "/useridList",
    new URLSearchParams({ source_name: PROPERTY_SOURCE_NAME }),
  );
  return data ?? null;
};

// POST /assessment — property's own detail contract (NOT motor's legacy
// /assessment_new, NOT motor's v2 GET /assessment). `random` is a cache-buster.
export const getPropertyAssessmentDetail = async (refNum) => {
  if (!refNum) throw new Error("getPropertyAssessmentDetail: ref_num is required");
  const { data } = await apiClient.post(
    "/assessment",
    new URLSearchParams({
      ref_num: refNum,
      source_name: PROPERTY_SOURCE_NAME,
      random: String(Date.now()),
    }),
  );
  return data ?? null;
};

// POST /vehicle — kept as getPropertyVehicle (matches the literal endpoint
// path) even though the record describes a property, not a vehicle.
export const getPropertyVehicle = async (refNum) => {
  if (!refNum) throw new Error("getPropertyVehicle: ref_num is required");
  const { data } = await apiClient.post(
    "/vehicle",
    new URLSearchParams({ ref_num: refNum, source_name: PROPERTY_SOURCE_NAME }),
  );
  return data ?? null;
};

/**
 * Property Insights — the AI property-image summary (CLIENT_GUIDE.md), a
 * separate, unauthenticated internal service (10.5.88.48:8010), not this
 * file's usual ergobalticproperty host.
 *
 * GET /get_summary (the public wrapper this used to call on the
 * ergobalticproperty host) started 404ing — whatever backend-side route or
 * record it depended on is gone. Rather than guess at a replacement wrapper,
 * this now talks to the documented service directly, the same way
 * camcomsales-prpt does it: no `apiClient` (different base, no Bearer token
 * this service wants or checks), and no ref_num lookup — the service has no
 * notion of "this app's cases" at all, only whatever photos are POSTed to it
 * in a given request. usePropertyAiSummary.js is what fetches a case's own
 * photos as bytes and submits them here.
 *
 * Proxied same-origin in both dev (setupProxy.js) and production
 * (nginx.conf) since the service sends no CORS headers and only answers
 * requests from inside the same network this app's host is also on.
 */
const PROPERTY_INSIGHTS_BASE_URL = "/property-insights-api";

/**
 * POST /v1/property-summaries → 202 { job_id, status, poll_url }.
 * `files` are actual File/Blob objects — this service takes bytes, not
 * URLs — already fetched by the caller. `country` is dropped if `region` is
 * given, matching the service's own "region overrides country" rule.
 */
export const submitPropertySummary = async ({ files, notes, country, region }) => {
  if (!files?.length) throw new Error("submitPropertySummary: at least one photo is required");
  const form = new FormData();
  files.forEach((file, i) => form.append("files", file, file.name || `photo${i + 1}.jpg`));
  if (notes) form.append("notes", notes);
  if (region) form.append("region", region);
  else if (country) form.append("country", country);

  const { data } = await axios.post(
    `${PROPERTY_INSIGHTS_BASE_URL}/v1/property-summaries`,
    form,
  );
  return data ?? null;
};

/**
 * GET /v1/jobs/{job_id} → the job's current state. 404 means an unknown id
 * or an expired one — results are only kept in memory for an hour after
 * completion (CLIENT_GUIDE.md), so a job checked too late looks identical to
 * one that never existed; usePropertyAiSummary.js is what turns either into
 * a "try again" state rather than a confusing raw 404.
 */
export const getPropertySummaryJob = async (jobId) => {
  if (!jobId) throw new Error("getPropertySummaryJob: job_id is required");
  const { data } = await axios.get(`${PROPERTY_INSIGHTS_BASE_URL}/v1/jobs/${jobId}`);
  return data ?? null;
};

/**
 * POST /download_master_data (multipart/form-data) — the zipped report
 * archive, confirmed against a real captured request on this host. Same
 * endpoint name Motor's Documents tab uses on its own host; the response
 * shape there is { url: <presigned ZIP URL> } and nothing here suggests
 * property's differs.
 *
 * apiClient defaults to application/x-www-form-urlencoded (see
 * apiService.js) for every other call in this file — explicitly clearing it
 * here lets axios/the browser set the multipart boundary FormData actually
 * needs instead of sending a header that lies about the body.
 */
export const downloadPropertyMasterData = async (refNum) => {
  if (!refNum) throw new Error("downloadPropertyMasterData: ref_num is required");
  const formData = new FormData();
  formData.append("ref_num", refNum);
  formData.append("source_name", PROPERTY_SOURCE_NAME);

  const { data } = await apiClient.post("/download_master_data", formData, {
    headers: { "Content-Type": undefined },
  });
  return data ?? null;
};
